import type {
  AnyConfigurationSection,
  ExtractSectionValues,
} from '@/teoCore/config/config-setup.types';
import { isTeoError } from '@/teoCore/errors/TeoError';
import { useClickOutside } from '@/ui/base/hooks/useClickOutside';

import type { ConfigPanelProps } from './AnalysisConfigPanel.utils';

export class ConfigPanel<TSection extends AnyConfigurationSection> {
  currentSection: TSection['id'] | 'all' | false = $state(false);
  currentValues: Partial<ExtractSectionValues<TSection>> = $state({});

  private readonly props: ConfigPanelProps<TSection>;

  constructor(props: ConfigPanelProps<TSection>) {
    this.props = props;

    if (props.values) {
      this.currentValues = { ...props.values };
    }
  }

  handleSectionSelect = (sectionId: TSection['id'] | 'all') => {
    if (sectionId === this.currentSection) {
      this.currentSection = false;
    } else {
      this.currentSection = sectionId;
    }
  };

  handleValueChange = (
    sectionId: TSection['id'],
    value: ExtractSectionValues<TSection>[TSection['id']],
  ) => {
    this.currentValues[sectionId] = value;
    // @ts-expect-error -- not sure why snapshot is not the same type as the currentValues
    this.props.onChange?.($state.snapshot(this.currentValues));
  };

  closeOnClickOutside = useClickOutside(() => {
    this.currentSection = false;
  });

  isLastSection(section: TSection) {
    if (!this.props.setup || isTeoError(this.props.setup)) {
      return false;
    }
    return section.id === this.props.setup?.sections[this.props.setup.sections.length - 1]?.id;
  }

  getTabTitle(section: TSection) {
    const value = this.currentValues[section.id];
    if (value) {
      return section.options.find((option) => option.id === value)?.label ?? section.name;
    }
    return section.title;
  }
}

export function createConfigPanel<TSection extends AnyConfigurationSection>(
  props: ConfigPanelProps<TSection>,
) {
  return new ConfigPanel(props);
}
